import React from 'react';

import Tippy from '@tippyjs/react';
import cs from 'classnames';

import { ia } from 'lib/helpers/utility';

import Avatar from '../Avatar/Avatar';

import AllAvatars from './components/AllAvatars';
import Hover from './components/Hover';
import { getClasses } from './lib/getClasses';
import { getFirstName, getLastName } from './lib/getName';

/**
 * @param {import('./lib/propTypes').AvatarGroupProps} AvatarGroupProps
 */

const AvatarGroup = ({
  items = [],
  itemsToShow = 4,
  size = 'medium',
  isCompact = true,
  isBorder = true,
  label = '',
  className,
  singleHoverPlacement = 'bottom',
  allHoverPlacement = 'bottom-end',
  itemsGroupBy = '',
  itemsGroupLabelClassName,
  simpleTooltip = false
}) => {
  const classes = getClasses(size, isBorder, isCompact);
  const displayedItems = items.slice(0, itemsToShow);

  if (!ia(items)) return null;

  const getTooltipContent = (item) => {
    const firstName = getFirstName(item);
    const lastName = getLastName(item);

    if (simpleTooltip) {
      return `${firstName} ${lastName}`.trim();
    }

    return (
      <Hover
        {...item}
        itemsGroupBy={itemsGroupBy}
        fontSize={classes.itemFontSize}
        firstName={firstName}
        lastName={lastName}
      />
    );
  };

  return (
    <div
      className={cs(
        'relative flex w-min select-none items-center rounded-full bg-white p-[2px] shadow-[0_1px_2px_rgba(27,33,35,0.03)] focus-within:bg-neutral-75 hover:bg-neutral-50',
        classes.wrapper,
        className
      )}>
      {isCompact && label && (
        <label
          className={cs(
            'font-600 capitalize leading-none text-neutral-600',
            size === 'small' || size === 'medium' ? 'mr-1' : 'mr-[6px]',
            classes.label,
            classes.labelMargin
          )}>
          {label}
        </label>
      )}
      <div className="flex">
        {displayedItems.map((item, index) => (
          <div
            key={item.id}
            className="relative"
            style={{
              marginLeft: index > 0 && -classes.size / 4,
              zIndex: index + 1
            }}>
            <Tippy
              className={cs(simpleTooltip ? 'tippy-dark' : 'custom-tooltip w-max max-w-[300px] py-2')}
              animation="fade"
              placement={singleHoverPlacement}
              arrow={simpleTooltip}
              content={getTooltipContent(item)}>
              <div>
                <Avatar
                  size={size}
                  fontSize={classes.itemFontSize}
                  firstName={getFirstName(item)}
                  lastName={getLastName(item)}
                  image={item.image}
                  isSquare={false}
                  id={item.id}
                  className="outline outline-2 outline-white"
                />
              </div>
            </Tippy>
          </div>
        ))}
      </div>
      <AllAvatars
        items={items}
        size={size}
        itemSize={classes.size}
        itemsToShow={itemsToShow}
        fontSize={classes.label}
        itemFontSize={classes.itemFontSize}
        isCompact={isCompact}
        placement={allHoverPlacement}
        itemsGroupBy={itemsGroupBy}
        itemsGroupLabelClassName={itemsGroupLabelClassName}
      />
    </div>
  );
};

export default AvatarGroup;
